import { useMemo, useState } from 'react';

import clsx from 'clsx';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import { Select } from './Select';
import { Toggle } from './Toggle';
import { TextField } from './TextField';
import { IBaseModel } from '../../types/system/BaseModel';

export interface ClientTableProps<T extends IBaseModel> {
  data: T[];
  columns: ColumnDef<T, any>[];
  enableGlobalFilter?: boolean;
  enableColumnFilters?: boolean;
  enableSorting?: boolean;
  rowsPerPage?: number;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function ClientTable<T extends IBaseModel>({
  data,
  columns,
  enableGlobalFilter = false,
  enableColumnFilters = false,
  enableSorting = false,
  rowsPerPage = 10,
  isLoading = false,
  emptyMessage = 'No data available',
}: ClientTableProps<T>) {
  const pageSizeOptions = [5, 10, 20, 30, 40, 50];
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPage,
  });

  // Filter data based on deleted status
  const filteredData = useMemo(() => {
    if (!includeDeleted) {
      return data.filter((item) => !item.deletedOn);
    }

    return data;
  }, [data, includeDeleted]);

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  // Custom global filter function that searches through searchable string fields
  const globalFilterFn = (row: any, columnId: string, value: string) => {
    if (!value) return true;

    const search = value.toLowerCase();
    const original = row.original;

    // Define searchable fields - prioritize string fields that are meaningful for search
    const searchableFields = [
      original.title,
      original.body,
      original.id,
      // Add any other direct string fields from your data
    ].filter((field) => field != null);

    // Convert to strings and search
    return searchableFields.some((field) =>
      String(field).toLowerCase().includes(search)
    );
  };

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      pagination,
      globalFilter,
      columnFilters,
      sorting,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableGlobalFilter: enableGlobalFilter,
    globalFilterFn: globalFilterFn,
    enableColumnFilters: enableColumnFilters,
    enableSorting: enableSorting,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-12 bg-gray-200 dark:bg-gray-700 rounded"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Global Search Filter */}
      {enableGlobalFilter && (
        <div className="mb-4">
          <div className="relative max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <TextField
              placeholder="Search..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.currentTarget.value)}
              className="pl-10"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="block overflow-x-auto overflow-y-hidden">
        <table className="min-w-full table border border-primary/40 divide-y divide-primary dark:border-primary">
          <thead className="bg-primary/40">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-3 py-3 text-left text-sm font-semibold"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={clsx(
                          enableSorting && header.column.getCanSort()
                            ? 'cursor-pointer select-none hover:text-primary-dark'
                            : ''
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {enableSorting &&
                          {
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted() as string]}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-primary/40">
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  className={clsx(
                    `${rowIndex % 2 !== 0 && 'bg-primary/10'}`,
                    row.original.deletedOn
                      ? 'bg-red-400/50 opacity-70 dark:text-white italic'
                      : ''
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="whitespace-nowrap px-3 py-4 text-sm"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <>
        <div className="w-full ml-auto mt-2 flex flex-col items-center space-y-2 lg:space-y-0 lg:flex-row lg:justify-between">
          <span className="flex items-center gap-1">
            <div>Page</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount() || 1}
            </strong>
          </span>
          <div>
            <button
              type="button"
              className={`rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 
                ${
                  !table.getCanPreviousPage() &&
                  'hover:!cursor-default !text-gray-400 hover:!bg-paper dark:!text-gray-500'
                }
                `}
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronDoubleLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 
                ${
                  !table.getCanPreviousPage() &&
                  'hover:!cursor-default !text-gray-400 hover:!bg-paper dark:!text-gray-500'
                }
                `}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 
                ${
                  !table.getCanNextPage() &&
                  'hover:!cursor-default !text-gray-400 hover:!bg-paper dark:!text-gray-500'
                }
                `}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 
                ${
                  !table.getCanNextPage() &&
                  'hover:!cursor-default !text-gray-400 hover:!bg-paper dark:!text-gray-500'
                }
                `}
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <ChevronDoubleRightIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <span>Per Page</span>
            <Select
              small
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.currentTarget.value));
              }}
            >
              {pageSizeOptions.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Showing {table.getRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} results
          {globalFilter && ` (filtered from ${filteredData.length} total)`}
        </div>
      </>
    </div>
  );
}
